import React from 'react';

import PropTypes from 'prop-types';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';

import { useTheme } from '@zenbusiness/zui/styles';
import useMediaQuery from '@zenbusiness/zui/useMediaQuery';

import { reportMarkdownComponents } from '#client/components/utils/markdownComponents';

import * as Styled from './ReportSection.style';

/**
 * Individual report section component with icon, title, cost range, and content
 * @param {object} props - Component props
 * @param {React.ReactElement} props.icon - Icon component for the section
 * @param {string} props.title - Section title
 * @param {string} props.content - Section content text (supports markdown)
 * @param {string} [props.costRange] - Cost range string (e.g., "$300 - $550")
 * @param props.isBubble
 * @param props.isTotal
 * @param props.showDisclaimer
 * @param props.disclaimer
 */
function ReportSection({
  icon,
  title,
  content,
  costRange,
  isBubble = false,
  showDisclaimer = false,
  disclaimer = ''
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Styled.ReportSectionRoot isBubble={isBubble}>
      <Styled.SectionHeader>
        <Styled.SectionTitleRow>
          <Styled.SectionTitleSection>
            {icon && <Styled.SectionIcon>{icon}</Styled.SectionIcon>}
            <Styled.SectionTitle>{title}</Styled.SectionTitle>
          </Styled.SectionTitleSection>
          {costRange && (
            <Styled.CostRange size={isMobile || costRange?.length > 45 ? 'sm' : 'md'}>
              {costRange}
            </Styled.CostRange>
          )}
        </Styled.SectionTitleRow>
      </Styled.SectionHeader>
      <Styled.SectionContent isBubble={isBubble} size="sm">
        <ReactMarkdown components={reportMarkdownComponents} rehypePlugins={[rehypeRaw]}>
          {showDisclaimer ? disclaimer : content}
        </ReactMarkdown>
      </Styled.SectionContent>
    </Styled.ReportSectionRoot>
  );
}

ReportSection.propTypes = {
  icon: PropTypes.element,
  title: PropTypes.string.isRequired,
  content: PropTypes.string,
  costRange: PropTypes.string,
  isBubble: PropTypes.bool,
  showDisclaimer: PropTypes.bool,
  disclaimer: PropTypes.string
};

export default ReportSection;
